import React from "react";
import { Box, Button } from "@mui/material";
import { alpha } from "@mui/material/styles";
import HistoryRounded from "@mui/icons-material/HistoryRounded";
import Flx from "../layout/Flx";
import Txt from "../typography/Txt";
import { APP_TOKENS } from "./publicFormTokens";

/**
 * Shown when a saved draft was found on mount. Restoring is opt-in: silently
 * repopulating six steps of answers from a previous visit would be startling,
 * and the browser may be shared.
 *
 * Three ways out, and they are not the same thing:
 *   Restore draft — load the saved answers.
 *   Start over    — delete the draft and begin clean.
 *   Ignore        — leave the draft alone but never ask about it again.
 */
const DraftRestoreBanner = ({ savedAt, onRestore, onDiscard, onIgnore }) => {
  const when = formatSavedAt(savedAt);

  return (
    <Box
      sx={(theme) => ({
        border: `1px solid ${theme.palette.primary.main}`,
        background: alpha(theme.palette.primary.main, 0.06),
        borderRadius: APP_TOKENS.radius,
        px: 2.5,
        py: 2,
      })}
    >
      <Flx between centerY gap={2} wrap>
        <Flx centerY gap={1.5}>
          <HistoryRounded sx={{ color: "primary.main", fontSize: 20 }} />
          <Flx column gap={0.25}>
            <Txt component="div" sx={{ fontWeight: 600, color: APP_TOKENS.ink }}>
              Pick up where you left off?
            </Txt>
            <Txt
              component="div"
              sx={{ fontSize: 12.5, color: APP_TOKENS.inkMuted }}
            >
              {when
                ? `We saved a draft of this form ${when}.`
                : "We saved a draft of this form in this browser."}
            </Txt>
          </Flx>
        </Flx>
        <Flx centerY gap={1}>
          {onIgnore && (
            <Button
              variant="text"
              size="small"
              onClick={onIgnore}
              sx={{ color: APP_TOKENS.inkMuted }}
            >
              Ignore
            </Button>
          )}
          <Button variant="text" size="small" onClick={onDiscard}>
            Start over
          </Button>
          <Button variant="contained" size="small" onClick={onRestore}>
            Restore draft
          </Button>
        </Flx>
      </Flx>
    </Box>
  );
};

/** Relative age of the draft, in the coarsest unit that's still meaningful. */
const formatSavedAt = (savedAt) => {
  if (!savedAt) return null;
  const then = new Date(savedAt).getTime();
  if (Number.isNaN(then)) return null;

  const minutes = Math.round((Date.now() - then) / 60000);
  if (minutes < 1) return "moments ago";
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;

  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
};

export default DraftRestoreBanner;
